import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { fetchConditionTargetCount } from "../../api/bulkInvestmentProposals";
import { getAdminLoginInfo } from "../../auth/accessToken";
import { SearchFormType, UserSearchEnvelopeRequestType } from "../../types/UsersSearch";
import { usersFormToRequest } from "../../utils/UserSearchProcessing";
import { getInterestedPropertyTypeNameMapWithUnset } from "../../utils/interestedPropertyTypes";
import { buildUserSearchSummaryParts } from "../../utils/userSearchSummary";
import { AdminVisibilitySettingsState } from "../../view_models/atoms";
import { useInterestedPropertyTypes } from "../master_data/useInterestedPropertyTypes";

export type BulkConditionStatus = "disabled" | "loading" | "ready" | "invalid";

interface UseBulkConditionTargetArgs {
  searchForm: SearchFormType;
  isReProposal: boolean;
  listPath: string;
}

interface UseBulkConditionTargetResult {
  isConditionMode: boolean;
  status: BulkConditionStatus;
  search: UserSearchEnvelopeRequestType | null;
  totalCount: number;
  summaryParts: string[];
}

// 条件は sessionStorage にしか無く URL には mode=condition しか乗らないため、
// URL共有・お気に入り経由・条件リセット後の遷移では条件が復元されず空条件になり得る。
// その場合は送信画面を出さず一覧へ戻す（サーバー側も 400 で弾く多層防御）。
export const useBulkConditionTarget = ({
  searchForm,
  isReProposal,
  listPath,
}: UseBulkConditionTargetArgs): UseBulkConditionTargetResult => {
  const location = useLocation();
  const navigate = useNavigate();
  const isConditionMode = new URLSearchParams(location.search).get("mode") === "condition";

  const isImpersonating = Boolean(getAdminLoginInfo());
  const adminVisibilitySettings = useRecoilValue(AdminVisibilitySettingsState);
  const showUserScore = isImpersonating && adminVisibilitySettings.showUserScore;
  const { tags: interestedPropertyTypes } = useInterestedPropertyTypes();

  // フォームが初期値と違うかでは足りない。useEstimatedLengthOfService は比較対象カラムを
  // 切り替えるだけの修飾フラグで、相方の lengthOfService が無いと絞り込みが効かないため。
  // サーバー側の NON_NARROWING_FILTER_KEYS と対で維持すること。
  const filters = useMemo(() => usersFormToRequest(searchForm), [searchForm]);

  const hasCondition = useMemo(() => {
    if (searchForm.userId) return true;
    return Object.keys(filters).some((key) => key !== "use_estimated_job_continuous_period");
  }, [filters, searchForm.userId]);

  const search: UserSearchEnvelopeRequestType | null = useMemo(() => {
    if (!isConditionMode || !hasCondition) return null;
    return {
      filters,
      keyword: searchForm.userId || undefined,
    };
  }, [isConditionMode, hasCondition, filters, searchForm.userId]);

  const summaryParts = useMemo(() => {
    if (!isConditionMode || !hasCondition) return [];
    return buildUserSearchSummaryParts(searchForm, {
      showUserScore,
      interestedPropertyTypeNameMap: getInterestedPropertyTypeNameMapWithUnset(interestedPropertyTypes),
    });
  }, [isConditionMode, hasCondition, searchForm, showUserScore, interestedPropertyTypes]);

  const [status, setStatus] = useState<BulkConditionStatus>(isConditionMode ? "loading" : "disabled");
  const [totalCount, setTotalCount] = useState(0);

  // 条件の中身が変わったときだけ件数を取り直すためのキー（search は毎レンダー新しい参照になる）
  const searchKey = search ? JSON.stringify(search) : null;

  useEffect(() => {
    if (!isConditionMode) {
      setStatus("disabled");
      return;
    }

    if (!search) {
      setStatus("invalid");
      alert("絞り込み条件が見つかりませんでした。\n一覧で条件を指定し直してください。");
      navigate(listPath, { replace: true });
      return;
    }

    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const count = await fetchConditionTargetCount(search, isReProposal);
        if (cancelled) return;
        setTotalCount(count);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        // 何名に送るか分からないまま送信させない。
        setStatus("invalid");
        alert("対象件数の取得に失敗しました。\n時間をおいて再度お試しください。");
        navigate(listPath, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isConditionMode, searchKey, search, isReProposal, listPath, navigate]);

  return { isConditionMode, status, search, totalCount, summaryParts };
};
